import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

function formatValueForMonday(type, rawValue, timeValue) {
  if (!rawValue) return null;
  switch (type) {
    case 'date': {
      // Monday.com interpretiert time als UTC – lokale Zeit in UTC umrechnen
      if (timeValue) {
        const [h, m] = timeValue.split(':').map(Number);
        const localDate = new Date(`${rawValue}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);
        const utcDate = localDate.toISOString().split('T')[0];
        const utcTime = localDate.toISOString().split('T')[1].substring(0, 8);
        return JSON.stringify({ date: utcDate, time: utcTime });
      }
      return JSON.stringify({ date: rawValue });
    }
    case 'numbers':
      return JSON.stringify(parseFloat(rawValue) || 0);
    case 'text':
    case 'long_text':
      return JSON.stringify(rawValue);
    case 'email':
      return JSON.stringify({ email: rawValue, text: rawValue });
    case 'phone':
      return JSON.stringify({ phone: rawValue, countryShortName: 'DE' });
    case 'link':
      return JSON.stringify({ url: rawValue, text: rawValue });
    case 'hour': {
      const [h, m] = rawValue.split(':');
      return JSON.stringify({ hour: parseInt(h), minute: parseInt(m || 0) });
    }
    case 'status':
    case 'color':
      return JSON.stringify({ label: rawValue });
    case 'boolean':
    case 'checkbox':
      return JSON.stringify({ checked: rawValue === 'true' || rawValue === true ? 'true' : 'false' });
    default:
      return JSON.stringify(rawValue);
  }
}

function getInputType(colType) {
  switch (colType) {
    case 'date': return 'date';
    case 'numbers': return 'number';
    case 'email': return 'email';
    case 'phone': return 'tel';
    case 'link': return 'url';
    case 'hour': return 'time';
    default: return 'text';
  }
}

function getPlaceholder(colType, colTitle) {
  switch (colType) {
    case 'date': return '';
    case 'numbers': return 'Zahl eingeben...';
    case 'email': return 'email@beispiel.de';
    case 'phone': return '+49 123 456789';
    case 'link': return 'https://...';
    case 'hour': return 'HH:MM';
    case 'status': return 'Status eingeben...';
    default: return `${colTitle} eingeben...`;
  }
}

function parseExistingValue(type, rawValue, currentValue) {
  const result = { value: '', time: '' };
  if (!rawValue && !currentValue) return result;

  try {
    const parsed = rawValue ? JSON.parse(rawValue) : null;

    switch (type) {
      case 'date':
        if (parsed?.date) {
          if (parsed.time) {
            // UTC → Lokalzeit umrechnen
            const utc = new Date(`${parsed.date}T${parsed.time}Z`);
            const localY = utc.getFullYear();
            const localM = String(utc.getMonth() + 1).padStart(2, '0');
            const localD = String(utc.getDate()).padStart(2, '0');
            const localH = String(utc.getHours()).padStart(2, '0');
            const localMin = String(utc.getMinutes()).padStart(2, '0');
            result.value = `${localY}-${localM}-${localD}`;
            result.time = `${localH}:${localMin}`;
          } else {
            result.value = parsed.date;
          }
        }
        break;
      case 'numbers':
        if (currentValue) result.value = currentValue;
        break;
      case 'email':
        if (parsed?.email) result.value = parsed.email;
        else if (currentValue) result.value = currentValue;
        break;
      case 'phone':
        if (parsed?.phone) result.value = parsed.phone;
        else if (currentValue) result.value = currentValue;
        break;
      case 'link':
        if (parsed?.url) result.value = parsed.url;
        else if (currentValue) result.value = currentValue;
        break;
      case 'hour':
        if (parsed?.hour !== undefined) {
          const h = String(parsed.hour).padStart(2, '0');
          const m = String(parsed.minute || 0).padStart(2, '0');
          result.value = `${h}:${m}`;
        }
        break;
      case 'status':
      case 'color':
        if (currentValue) result.value = currentValue;
        break;
      case 'boolean':
      case 'checkbox':
        if (parsed?.checked === 'true' || parsed?.checked === true) result.value = 'true';
        else result.value = 'false';
        break;
      case 'text':
      case 'long_text':
      default:
        if (currentValue) result.value = currentValue;
        break;
    }
  } catch {
    if (currentValue) result.value = currentValue;
  }

  return result;
}

function ExternalForm() {
  const { token } = useParams();
  const [formData, setFormData] = useState(null);
  const [values, setValues] = useState({});
  const [timeValues, setTimeValues] = useState({});
  const [fileValues, setFileValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [results, setResults] = useState([]);

  const loadFormData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/external/form-data/${token}`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Fehler beim Laden');
      }
      const data = await res.json();
      setFormData(data);

      const initial = {};
      const initialTimes = {};
      data.items.forEach(item => {
        item.columns.forEach(col => {
          const key = `${item.id}_${col.id}`;
          const parsed = parseExistingValue(col.type, col.rawValue, col.currentValue);
          initial[key] = parsed.value;
          if (col.type === 'date') {
            initialTimes[key] = parsed.time || '';
          }
        });
      });
      setValues(initial);
      setTimeValues(initialTimes);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);

  const handleChange = (itemId, colId, value) => {
    setValues(prev => ({
      ...prev,
      [`${itemId}_${colId}`]: value
    }));
  };

  const handleTimeChange = (itemId, colId, value) => {
    setTimeValues(prev => ({
      ...prev,
      [`${itemId}_${colId}`]: value
    }));
  };

  const handleFileChange = (itemId, colId, files) => {
    setFileValues(prev => ({
      ...prev,
      [`${itemId}_${colId}`]: files ? Array.from(files) : []
    }));
  };

  const removeFile = (itemId, colId, index) => {
    setFileValues(prev => {
      const key = `${itemId}_${colId}`;
      const current = [...(prev[key] || [])];
      current.splice(index, 1);
      return { ...prev, [key]: current };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setResults([]);

    const updates = [];

    // Validierung: Uhrzeit verpflichtend prüfen
    if (formData.timeRequired) {
      let timeMissing = false;
      formData.items.forEach(item => {
        item.columns.forEach(col => {
          if (col.type === 'date') {
            const dateVal = values[`${item.id}_${col.id}`];
            const timeVal = timeValues[`${item.id}_${col.id}`];
            if (dateVal && !timeVal) {
              timeMissing = true;
            }
          }
        });
      });
      if (timeMissing) {
        setError('Bitte geben Sie auch die Uhrzeit für alle Datumsfelder an.');
        setSubmitting(false);
        return;
      }
    }

    // Datei-Uploads sammeln
    const fileUploads = [];
    formData.items.forEach(item => {
      item.columns.forEach(col => {
        if (col.type === 'file') {
          const files = fileValues[`${item.id}_${col.id}`] || [];
          files.forEach(file => {
            fileUploads.push({ itemId: item.id, columnId: col.id, file });
          });
        }
      });
    });

    formData.items.forEach(item => {
      item.columns.forEach(col => {
        if (col.type === 'file') return; // Dateien werden separat hochgeladen
        const rawValue = values[`${item.id}_${col.id}`];
        if (rawValue) {
          const timeVal = col.type === 'date' ? timeValues[`${item.id}_${col.id}`] : undefined;
          const formattedValue = formatValueForMonday(col.type, rawValue, timeVal);
          if (formattedValue) {
            updates.push({
              itemId: item.id,
              columnId: col.id,
              value: formattedValue
            });
          }
        }
      });
    });

    if (updates.length === 0 && fileUploads.length === 0) {
      setError('Bitte füllen Sie mindestens ein Feld aus oder laden Sie eine Datei hoch.');
      setSubmitting(false);
      return;
    }

    try {
      const allResults = [];

      // Normale Feld-Updates
      if (updates.length > 0) {
        const res = await fetch(`${API_BASE}/api/external/update/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        allResults.push(...data.results);
      }

      // Datei-Uploads
      for (const upload of fileUploads) {
        try {
          const fd = new FormData();
          fd.append('file', upload.file);
          fd.append('itemId', upload.itemId);
          fd.append('columnId', upload.columnId);

          const res = await fetch(`${API_BASE}/api/external/upload-file/${token}`, {
            method: 'POST',
            body: fd
          });
          const data = await res.json();
          allResults.push({
            itemId: upload.itemId,
            columnId: upload.columnId,
            success: data.success,
            error: data.error,
            fileName: upload.file.name
          });
        } catch (err) {
          allResults.push({
            itemId: upload.itemId,
            columnId: upload.columnId,
            success: false,
            error: err.message,
            fileName: upload.file.name
          });
        }
      }

      setResults(allResults);
      const allSuccess = allResults.every(r => r.success);
      if (allSuccess) {
        setSuccess(true);
      } else {
        const failures = allResults.filter(r => !r.success);
        setError(`${failures.length} Aktualisierung(en) fehlgeschlagen`);
      }
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="external-container">
        <div className="loading">
          <span className="spinner"></span>
          Formular wird geladen...
        </div>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="external-container">
        <div className="external-form">
          <div className="external-header">
            <div className="logo">⚠️</div>
            <h1>Link ungültig</h1>
            <p>{error}</p>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ color: '#676879' }}>
              Dieser Link ist möglicherweise abgelaufen oder wurde widerrufen.
              Bitte kontaktieren Sie die Person, die Ihnen den Link gesendet hat.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="external-container">
        <div className="external-form">
          <div className="external-header">
            <div className="logo" style={{ background: 'linear-gradient(135deg, #00ca72, #00a85a)' }}>✓</div>
            <h1>Vielen Dank!</h1>
            <p>Ihre Daten wurden erfolgreich übermittelt.</p>
          </div>
          <div className="card">
            <div className="alert alert-success">
              Alle {results.filter(r => r.success).length} Feld(er) wurden erfolgreich aktualisiert.
            </div>
            <p style={{ textAlign: 'center', color: '#676879', fontSize: '14px' }}>
              Sie können dieses Fenster nun schließen.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="external-container">
      <div className="external-form">
        <div className="external-header">
          <div className="logo">📋</div>
          <h1>{formData.boardName || 'Daten aktualisieren'}</h1>
          {formData.description && <p>{formData.description}</p>}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {formData.items.map(item => (
            <div key={item.id} className="item-card">
              <div className="item-title">
                <span className="fin-badge">{item.name}</span>
              </div>

              {item.columns.map(col => (
                <div key={col.id} className="form-group">
                  <label>
                    {col.title}
                    {col.currentValue && (
                      <span style={{ fontWeight: 400, color: '#676879', fontSize: '12px', marginLeft: '8px' }}>
                        (Aktuell: {col.currentValue})
                      </span>
                    )}
                  </label>
                  {col.type === 'long_text' ? (
                    <textarea
                      value={values[`${item.id}_${col.id}`] || ''}
                      onChange={e => handleChange(item.id, col.id, e.target.value)}
                      placeholder={getPlaceholder(col.type, col.title)}
                      rows={3}
                    />
                  ) : col.type === 'date' ? (
                    <>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="date"
                          value={values[`${item.id}_${col.id}`] || ''}
                          onChange={e => handleChange(item.id, col.id, e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="time"
                          value={timeValues[`${item.id}_${col.id}`] || ''}
                          onChange={e => handleTimeChange(item.id, col.id, e.target.value)}
                          style={{ flex: 1 }}
                          placeholder="HH:MM"
                        />
                      </div>
                      <span style={{ fontSize: '12px', color: '#676879', marginTop: '4px', display: 'block' }}>
                        {formData.timeRequired
                          ? '⏰ Datum und Uhrzeit sind erforderlich'
                          : '⏰ Uhrzeit ist optional'}
                      </span>
                    </>
                  ) : col.type === 'file' ? (
                    <>
                      <div
                        className="file-drop-zone"
                        onClick={() => document.getElementById(`file-${item.id}-${col.id}`).click()}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                        onDragLeave={e => { e.currentTarget.classList.remove('drag-over'); }}
                        onDrop={e => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('drag-over');
                          const existing = fileValues[`${item.id}_${col.id}`] || [];
                          handleFileChange(item.id, col.id, [...existing, ...Array.from(e.dataTransfer.files)]);
                        }}
                      >
                        <input
                          id={`file-${item.id}-${col.id}`}
                          type="file"
                          multiple
                          style={{ display: 'none' }}
                          onChange={e => {
                            const existing = fileValues[`${item.id}_${col.id}`] || [];
                            handleFileChange(item.id, col.id, [...existing, ...Array.from(e.target.files)]);
                            e.target.value = '';
                          }}
                        />
                        <div style={{ fontSize: '28px', marginBottom: '6px' }}>📎</div>
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>Dateien hierher ziehen oder klicken</div>
                        <div style={{ fontSize: '12px', color: '#676879', marginTop: '4px' }}>Max. 50 MB pro Datei</div>
                      </div>
                      {(fileValues[`${item.id}_${col.id}`] || []).length > 0 && (
                        <div className="file-list">
                          {(fileValues[`${item.id}_${col.id}`] || []).map((file, idx) => (
                            <div key={idx} className="file-item">
                              <span className="file-name">{file.name}</span>
                              <span style={{ fontSize: '12px', color: '#676879' }}>
                                {(file.size / 1024).toFixed(0)} KB
                              </span>
                              <button
                                type="button"
                                className="file-remove"
                                onClick={() => removeFile(item.id, col.id, idx)}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (col.type === 'status' || col.type === 'color') && col.statusLabels ? (
                    <select
                      value={values[`${item.id}_${col.id}`] || ''}
                      onChange={e => handleChange(item.id, col.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="">— Status wählen —</option>
                      {col.statusLabels.map((label, idx) => (
                        <option key={idx} value={label}>{label}</option>
                      ))}
                    </select>
                  ) : (col.type === 'boolean' || col.type === 'checkbox') ? (
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={values[`${item.id}_${col.id}`] === 'true' || values[`${item.id}_${col.id}`] === true}
                        onChange={e => handleChange(item.id, col.id, e.target.checked ? 'true' : 'false')}
                      />
                      <span className="checkbox-text">{col.title}</span>
                    </label>
                  ) : (
                    <input
                      type={getInputType(col.type)}
                      value={values[`${item.id}_${col.id}`] || ''}
                      onChange={e => handleChange(item.id, col.id, e.target.value)}
                      placeholder={getPlaceholder(col.type, col.title)}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: '16px', marginTop: '8px' }}
          >
            {submitting ? (
              <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }}></span> Wird übermittelt...</>
            ) : (
              '✅ Daten übermitteln'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ExternalForm;
